/**
 * Runtime config — read once at startup so the same JS bundle can point
 * at different backends without rebuilding. Values come from (in order):
 *
 * 1. `window.__EDGE_RUNTIME_CONFIG__` — populated by `/runtime-config.js`,
 *    a small script loaded before the app bundle. The web app ships an
 *    empty stub; ops or the native shell can override per-environment.
 * 2. `import.meta.env.VITE_*` — Vite build-time env fallback.
 *
 * The wrapper keeps consumers synchronous: callers still import constants
 * from `@/config/constants`, which uses `getRuntimeValue` under the hood.
 */

type RuntimeConfig = Partial<Record<string, string | boolean>>

declare global {
  interface Window {
    __EDGE_RUNTIME_CONFIG__?: RuntimeConfig
  }
}

function readRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return {}
  return window.__EDGE_RUNTIME_CONFIG__ ?? {}
}

/**
 * Read a config value, preferring runtime override over Vite env.
 * Returns `undefined` if neither source supplies the key.
 */
export function getRuntimeValue(key: string): string | undefined {
  const fromRuntime = readRuntimeConfig()[key]
  if (typeof fromRuntime === 'string') return fromRuntime
  const fromEnv = (import.meta.env as Record<string, unknown>)[key]
  if (typeof fromEnv === 'string') return fromEnv
  return undefined
}

export function getRuntimeBool(key: string): boolean {
  const fromRuntime = readRuntimeConfig()[key]
  if (typeof fromRuntime === 'boolean') return fromRuntime
  if (typeof fromRuntime === 'string') return fromRuntime === 'true'
  const fromEnv = (import.meta.env as Record<string, unknown>)[key]
  return fromEnv === 'true' || fromEnv === true
}
